/* import __COLOCATED_TEMPLATE__ from './seller-posting-pitch-form.hbs'; */
import Component from '@glimmer/component';
import { action, getProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { scrollToError } from 'sh-www/utils/handles-errors-v2';
import { validateSchema } from 'sh-www/utils/validators';
import Validator from 'validator';
import { boolean, object, string } from 'yup';

const QUESTION_SCHEMA = string()
  .required('You must respond to this question.')
  .max(500, 'Your response must be 500 characters or less.');

const BASE_SCHEMA = {
  'headline': string()
    .required('You must provide a headline.'),
  'summary': string()
    .required('You must provide a summary of what your story is about.')
    .max(500, 'Your summary must be 500 characters or less.'),
  'characters': string()
    .required('You must provide information about the characters within the production.')
    .max(500, 'The character list must be 500 characters or less.'),
  'location': object()
    .required('You must select a location where this takes place.'),
  'produced': boolean()
    .required('You must answer whether you have already produced a video for this story.'),
  'production_link': string()
    .test(
      'link-requirement',
      'Link must be a valid URL',
      function(value) {
        const protocols = ['http', 'https'];
        return value ? Validator.isURL(value, { protocols }) : true;
      }
    ),
};

const hasErrors = (errors) => {
  return Object.values(errors).some((error) => error !== undefined);
}

const normalizeErrorPunctuation = (errorValues) => {
  return errorValues.map((errorSentence) => {
    if (errorSentence.endsWith('.')) {
        return errorSentence.slice(0, -1);
    }

    return errorSentence;
  });
}

/** @module */
class SellerPostingPitchForm extends Component {
  @service('error-tracker') errorTracker;

  @tracked errors = {};
  @tracked questionResponses = this.args.data.assignmentQuestions.reduce((accum, { id }) => ({ ...accum, [id]: '' }), {});
  @tracked headline = '';
  @tracked location = null;
  @tracked locationInputValue = '';
  @tracked summary = '';
  @tracked characters = '';
  @tracked produced = null;
  @tracked production_link = '';

  /**
   * Dynamic schema for yup validation
   */
  get schema() {
    const questionIds = (this.args.data.assignmentQuestions ?? []).map(({ id }) => id);

    return object({
      ...BASE_SCHEMA,
      questionResponses: object().shape(
        questionIds.reduce((accum, questionId) => {
          accum[questionId] = QUESTION_SCHEMA;

          return accum;
        }, {}),
      ),
    });
  }

  /**
   * Maps a question it to an array of errors associated with it
   */
  get questionIdToErrorsMap() {
    return Object.entries(this.errors).reduce((accum, [errorKey, errorValues]) => {
      if (
        (
          // server error or yup validation error
          errorKey.startsWith('assignment_question_responses')
          || errorKey.startsWith('questionResponses')
        )
        && errorValues
      ) {
        const questionId = errorKey.split('.')[1];

        // 'assignment_question_responses' handled below form
        if (questionId === undefined) {
          return accum;
        }

        // yup or server error
        const errorArray = Array.isArray(errorValues)
          ? errorValues
          : [errorValues];

        // combine question error with any others found
        accum[questionId] = [
          ...(accum[questionId] ?? []),
          ...errorArray,
        ];
      }

      return accum;
    }, {});
  }

  /**
   * Standard interface for accessing errors on questions
   */
  get questionErrors() {
    const { questionIdToErrorsMap } = this;
    return Object.entries(questionIdToErrorsMap).reduce((accum, [errorKey, errorValues]) => {
      accum[errorKey] = normalizeErrorPunctuation(errorValues).join('. ');

      return accum;
    }, {});
  }

  @action
  updateField(field, value) {
    this[field] = value;

    const errors = {
      ...this.errors,
      [field]: undefined,
    };

    // clear related errors
    if (field === 'produced') {
      errors.production_link = undefined;
    } else if (field === 'production_link') {
      errors.produced = undefined;
    }

    this.errors = errors;
  }

  /**
   * @param {Location} location
   */
  @action
  updateLocation(location) {
    this.location = location;
    this.locationInputValue = location?.formattedAddress ?? '';

    this.errors = {
      ...this.errors,
      location_id: undefined,
    };
  }

  /**
   * @param {string} questionId
   * @param {string} value
   */
  @action
  updateQuestionResponse(questionId, value) {
    this.questionResponses = {
      ...this.questionResponses,
      [questionId]: value,
    };

    this.errors = {
      ...this.errors,
      assignment_question_responses: undefined,
      [`assignment_question_responses.${questionId}`]: undefined,
      [`questionResponses.${questionId}`]: undefined,
    };
  }

  /**
   *
   */
  @dropTask
  *handleSubmit() {
    this.errors = {};

    const { schema } = this;
    const { fields } = schema;
    const properties = getProperties(this, Object.keys(fields));

    // validate before submitting
    const [_, validationErrors = {}] = validateSchema(properties, schema);

    // transform errors
    if (validationErrors.location) {
      validationErrors.location_id = validationErrors.location;
      delete validationErrors.location;
    }

    // set errors based on validation
    this.errors = validationErrors;

    // prevent submit while errors exist
    if (hasErrors(validationErrors)) {
      scrollToError(this, validationErrors);

      return;
    }

    const {
      questionResponses: questionIdToResponseMap,
      headline,
      location,
      summary,
      characters,
      photo,
      produced,
      production_link,
    } = this;

    yield this.args.onsubmit({
      questionIdToResponseMap,
      headline,
      location,
      summary,
      characters,
      photo,
      produced,
      production_link,
    }).catch((error) => {
      const responseErrors = Object.entries(error.errors).reduce((accum, [errorKey, errorValues]) => {
        if (errorKey === 'generic-exception') {
          this.errorTracker.error('Failed to create pitch', this, error);

          accum[errorKey] = 'Failed to create pitch. Please refresh and try again or contact support.';
        } else {
          accum[errorKey] = errorValues;
        }

        return accum;
      }, {});

      this.errors = responseErrors;

      scrollToError(this, responseErrors);
    });
  }
}

export default SellerPostingPitchForm;
